/**
 * Physical PV mounting configuration (engineering data, stored with the structure):
 *  - module side: modules ABOVE the support line (rail / purlin under the module — the usual arrangement) or BELOW it
 *    (modules hung under the rails, e.g. underslung canopies);
 *  - rail layer on framed structures: modules clamped directly on purlins, on rails crossing the purlins, or on mini
 *    rails sitting on the purlins.
 *
 * Which combinations are geometrically possible depends on the structure type (MOUNTING_RULES). A structure may be
 * further restricted to what the selected mounting product's documentation allows (`instance.mounting`): the tool
 * does not invent product rules — it only applies the restrictions the engineer enters.
 *
 * This is a VISUALISATION-INDEPENDENT model: exploded / inspection views never change these values.
 */
import { z } from 'zod';
import type { StructureKind } from './kinds';

export const MODULE_SIDES = ['ABOVE', 'BELOW'] as const;
export type ModuleSide = (typeof MODULE_SIDES)[number];
export const MODULE_SIDE_LABEL: Record<ModuleSide, string> = {
  ABOVE: 'Tấm pin phía trên thanh đỡ',
  BELOW: 'Tấm pin phía dưới thanh đỡ (lắp treo)',
};

export const RAIL_LAYERS = ['NONE', 'RAIL', 'MINI_RAIL'] as const;
export type RailLayer = (typeof RAIL_LAYERS)[number];
export const RAIL_LAYER_LABEL: Record<RailLayer, string> = {
  NONE: 'Kẹp trực tiếp lên xà gồ',
  RAIL: 'Rail vuông góc xà gồ',
  MINI_RAIL: 'Mini rail trên xà gồ',
};

export interface MountingRule {
  moduleSides: ModuleSide[];
  railLayers: RailLayer[];
  /** Why an option is not available for this structure type */
  reasons: Partial<Record<ModuleSide | RailLayer, string>>;
}

const FRAME_BELOW =
  'Chỉ dùng khi tấm pin và kẹp được nhà sản xuất cho phép lắp treo; cột phải nằm ngoài vùng tấm (công cụ kiểm tra va chạm).';

/** Geometric possibilities per structure type (not product approvals). */
export const MOUNTING_RULES: Record<StructureKind, MountingRule> = {
  ROOF_RAIL: {
    moduleSides: ['ABOVE'],
    railLayers: ['NONE'],
    reasons: { BELOW: 'Rail nằm sát mặt mái — không có khoảng để treo tấm dưới rail.' },
  },
  MINI_RAIL: {
    moduleSides: ['ABOVE'],
    railLayers: ['NONE'],
    reasons: { BELOW: 'Mini rail bắt trực tiếp lên mái — tấm chỉ có thể nằm phía trên.' },
  },
  TILT_FRAME: {
    moduleSides: ['ABOVE'],
    railLayers: ['NONE', 'RAIL', 'MINI_RAIL'],
    reasons: { BELOW: 'Khung nghiêng thấp: chân khung nằm dưới tấm, không treo tấm được.' },
  },
  ELEVATED_ROW: {
    moduleSides: ['ABOVE'],
    railLayers: ['NONE', 'RAIL', 'MINI_RAIL'],
    reasons: { BELOW: 'Khung một hàng có cột trong vùng tấm — không treo tấm dưới xà gồ được.' },
  },
  ELEVATED_MULTI: { moduleSides: ['ABOVE', 'BELOW'], railLayers: ['NONE', 'RAIL', 'MINI_RAIL'], reasons: { BELOW: FRAME_BELOW } },
  PORTAL: { moduleSides: ['ABOVE', 'BELOW'], railLayers: ['NONE', 'RAIL', 'MINI_RAIL'], reasons: { BELOW: FRAME_BELOW } },
  COLUMN_BEAM: { moduleSides: ['ABOVE', 'BELOW'], railLayers: ['NONE', 'RAIL', 'MINI_RAIL'], reasons: { BELOW: FRAME_BELOW } },
  CUSTOM: { moduleSides: ['ABOVE', 'BELOW'], railLayers: ['NONE', 'RAIL', 'MINI_RAIL'], reasons: { BELOW: FRAME_BELOW } },
  CARPORT: { moduleSides: ['ABOVE', 'BELOW'], railLayers: ['NONE', 'RAIL', 'MINI_RAIL'], reasons: { BELOW: FRAME_BELOW } },
};

/** Restrictions from the mounting product's documentation (entered by the engineer). */
export const mountingRestrictionSchema = z
  .object({
    moduleSides: z.array(z.enum(MODULE_SIDES)).min(1).max(2).optional(),
    railLayers: z.array(z.enum(RAIL_LAYERS)).min(1).max(3).optional(),
    /** Document reference (datasheet / installation manual) */
    reference: z.string().max(200).optional(),
  })
  .loose();
export type MountingRestriction = z.infer<typeof mountingRestrictionSchema>;

export interface MountingOption<T extends string> {
  value: T;
  allowed: boolean;
  reason?: string;
}

/** Options for the UI and the generator: allowed = type rule ∩ product restriction. */
export function mountingOptions(
  kind: StructureKind,
  restriction?: MountingRestriction | null,
): { sides: MountingOption<ModuleSide>[]; layers: MountingOption<RailLayer>[] } {
  const rule = MOUNTING_RULES[kind];
  const ref = restriction?.reference ? ` (${restriction.reference})` : '';
  const sides = MODULE_SIDES.map((v): MountingOption<ModuleSide> => {
    if (!rule.moduleSides.includes(v)) return { value: v, allowed: false, reason: rule.reasons[v] ?? 'Không áp dụng cho loại kết cấu này.' };
    if (restriction?.moduleSides && !restriction.moduleSides.includes(v))
      return { value: v, allowed: false, reason: `Hệ lắp đặt đã chọn không cho phép${ref}.` };
    return { value: v, allowed: true, reason: rule.reasons[v] };
  });
  const layers = RAIL_LAYERS.map((v): MountingOption<RailLayer> => {
    if (!rule.railLayers.includes(v)) return { value: v, allowed: false, reason: rule.reasons[v] ?? 'Không áp dụng cho loại kết cấu này.' };
    if (restriction?.railLayers && !restriction.railLayers.includes(v))
      return { value: v, allowed: false, reason: `Hệ lắp đặt đã chọn không cho phép${ref}.` };
    return { value: v, allowed: true };
  });
  return { sides, layers };
}

/** Effective configuration: requested values when allowed, otherwise the first allowed value + the reason. */
export function resolveMounting(
  kind: StructureKind,
  restriction: MountingRestriction | null | undefined,
  requested: { side?: unknown; layer?: unknown },
): { side: ModuleSide; layer: RailLayer; problems: string[] } {
  const o = mountingOptions(kind, restriction);
  const problems: string[] = [];
  const pick = <T extends string>(opts: MountingOption<T>[], want: unknown, label: Record<T, string>): T => {
    const hit = opts.find((x) => x.value === want);
    if (hit?.allowed) return hit.value;
    const first = opts.find((x) => x.allowed) ?? opts[0]!;
    if (hit) problems.push(`Cấu hình “${label[hit.value]}” không được hỗ trợ: ${hit.reason ?? ''} Đang dùng “${label[first.value]}”.`);
    return first.value;
  };
  return {
    side: pick(o.sides, requested.side ?? 'ABOVE', MODULE_SIDE_LABEL),
    layer: pick(o.layers, requested.layer ?? 'NONE', RAIL_LAYER_LABEL),
    problems,
  };
}
